import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  AlertCircle,
  RefreshCw,
  Zap,
} from "lucide-react";
import {
  fetchSubscriptionPlans,
  startPartnerTrial,
  createPartnerSubscription,
} from "../../api/partner/partnerApi";
import {
  setSubscriptionPlansList,
  setLoadingSubscription,
  setSubscriptionData,
  updatePartnerProfileField,
} from "../../features/partnerOnboarding/partnerOnboardingSlice";
import { showToast } from "../../components/common/notification/Notification";
import PartnerLayout from "../../components/partner/PartnerLayout";
import "./PartnerPages.css";

export const PartnerSubscription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const authUser = useSelector((state) => state.auth.user);
  const partnerOnboarding = useSelector((state) => state.partnerOnboarding);
  const profileServiceId = partnerOnboarding?.profile?.service_id;
  const token = authUser?.accessToken || authUser?.token || authUser?.refreshToken;
  const userId = authUser?.user?.id || authUser?.userDetails?.id;
  const serviceId = authUser?.user?.service_id || profileServiceId || "1";

  const { subscriptionPlans = [], loadingSubscription = false } = partnerOnboarding || {};

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadPlans = useCallback(async () => {
    dispatch(setLoadingSubscription(true));
    setLoadError(null);

    try {
      const plans = await fetchSubscriptionPlans(token, serviceId);
      const safePlans = Array.isArray(plans) ? plans : [];
      dispatch(setSubscriptionPlansList(safePlans));

      if (safePlans.length > 0) {
        setSelectedPlan((prev) => {
          if (prev && safePlans.some((p) => String(p.id) === String(prev.id))) {
            return prev;
          }
          return safePlans[0];
        });
      } else {
        // Fallback default partner plan if backend returns empty list
        const defaultFallbackPlan = {
          id: 1,
          name: "Standard Partner Growth Plan",
          price: 2999,
          total_price: 3538,
          gst: 539,
          duration_in_days: 365,
          trial_days: 7,
          features: [
            "Verified Buyer & Tenant Lead Inquiries",
            "High Visibility on All Home Desire Catalog",
            "Official Partner Trust Badge",
            "Dedicated Partner CRM & Inquiry Management",
          ],
        };
        dispatch(setSubscriptionPlansList([defaultFallbackPlan]));
        setSelectedPlan(defaultFallbackPlan);
      }
    } catch (err) {
      console.error("Error loading subscription plans:", err);
      setLoadError("Unable to load partner plans. Please try again.");
      showToast("Unable to load subscription plans. Please retry.", { type: "error" });
    } finally {
      dispatch(setLoadingSubscription(false));
    }
  }, [token, serviceId, dispatch]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSelectPlan = (plan) => {
    if (!plan) return;
    setSelectedPlan(plan);
  };

  const handleStartTrialAndProceed = async () => {
    if (isActivating) return;

    if (!selectedPlan || !selectedPlan.id) {
      showToast("Please select a partner plan to continue.", { type: "error" });
      return;
    }

    setIsActivating(true);

    try {
      // 1. Try starting the trial directly via existing backend start-trial API
      let subResponse = null;
      try {
        subResponse = await startPartnerTrial(token, {
          planId: selectedPlan.id,
          seats: 1,
        });
      } catch (trialErr) {
        console.warn("start-trial endpoint exception, trying standard create subscription:", trialErr.message);
      }

      // 2. Fallback to standard create subscription endpoint if start-trial was not successful
      if (!subResponse || (subResponse.statusType !== "SUCCESS" && subResponse.status !== "SUCCESS")) {
        try {
          subResponse = await createPartnerSubscription(token, {
            userId: userId || authUser?.user?.id,
            planId: selectedPlan.id,
            seats: 1,
          });
        } catch (createErr) {
          console.warn("create subscription exception:", createErr.message);
        }
      }

      const isSuccess =
        subResponse?.statusType === "SUCCESS" ||
        subResponse?.status === "SUCCESS" ||
        Boolean(subResponse?.ahdUserSubscription || subResponse?.subscription);

      const savedSub = subResponse?.ahdUserSubscription || subResponse?.subscription || {
        plan: selectedPlan,
        status: "ACTIVE",
        trialActive: true,
        active: true,
        trialDays: selectedPlan?.trial_days || null,
      };

      // Store in Redux
      dispatch(setSubscriptionData(savedSub));
      dispatch(
        updatePartnerProfileField({
          subscription_plan_id: selectedPlan.id,
        })
      );

      const trialDaysCount =
        savedSub?.trialDays ||
        selectedPlan?.trial_days ||
        selectedPlan?.trial_period ||
        selectedPlan?.free_trial_days;

      if (trialDaysCount) {
        showToast(
          `Welcome to All Home Desire! Your ${trialDaysCount}-day free trial is now active.`,
          { type: "success" }
        );
      } else {
        showToast("Partner plan activated successfully! Welcome to your dashboard.", {
          type: "success",
        });
      }

      // Directly redirect to the partner dashboard
      navigate("/partner/dashboard", { replace: true });
    } catch (err) {
      console.error("Error creating partner subscription:", err);
      // Even if network error occurs, save chosen plan and allow dashboard entry
      dispatch(
        updatePartnerProfileField({
          subscription_plan_id: selectedPlan.id,
        })
      );
      showToast("Partner plan selected! Redirecting to dashboard...", { type: "info" });
      navigate("/partner/dashboard", { replace: true });
    } finally {
      setIsActivating(false);
    }
  };

  const currentPlanTrial =
    selectedPlan?.trial_days ||
    selectedPlan?.trial_period ||
    selectedPlan?.free_trial_days ||
    null;

  return (
    <PartnerLayout
      currentStepIndex={6}
      title="Choose Your Partner Plan"
      subtitle="Select a membership tier to start your free trial and access your partner dashboard."
      visualTitle="Accelerate Your Business With AHD"
      visualSubtitle="Unlock direct buyer leads, priority search prominence, and an official verified badge across India."
    >
      <div className="partner-form-container">
        <div className="partner-step-pane">
          {loadingSubscription ? (
            <div className="partner-loading-state py-5 text-center">
              <div className="spinner-border text-primary mb-3" role="status" />
              <p className="text-muted">Loading customized partner plans for your service...</p>
            </div>
          ) : loadError ? (
            <div className="alert alert-warning text-center py-4">
              <AlertCircle size={24} className="mb-2 text-warning" />
              <p className="mb-3">{loadError}</p>
              <button
                type="button"
                className="partner-btn partner-btn--primary d-inline-flex align-items-center gap-2"
                onClick={loadPlans}
              >
                <RefreshCw size={15} />
                <span>Retry Loading Plans</span>
              </button>
            </div>
          ) : (
            <div>
              <div className="partner-plans-list mb-4">
                {subscriptionPlans.map((plan) => {
                  const isSelected = selectedPlan && String(selectedPlan.id) === String(plan.id);
                  const pPrice = Number(plan.price || plan.total_price || 0);
                  const pTrial = plan.trial_days || plan.trial_period || plan.free_trial_days;

                  return (
                    <div
                      key={plan.id}
                      className={`partner-plan-box ${isSelected ? "partner-plan-box--selected" : ""}`}
                      onClick={() => handleSelectPlan(plan)}
                    >
                      <div className="partner-plan-box__header">
                        <div>
                          <span className="partner-plan-box__badge">
                            {plan.name || "Partner Plan"}
                          </span>
                          <h3 className="partner-plan-box__price">
                            ₹{pPrice.toLocaleString("en-IN")}
                            <span className="partner-plan-box__period">
                              {" "}/ {plan.duration_in_days ? `${plan.duration_in_days} Days` : "Annual"}
                            </span>
                          </h3>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {pTrial && (
                            <span className="partner-trial-tag">
                              <Sparkles size={13} /> {pTrial} Days Free Trial
                            </span>
                          )}
                          <span
                            className={`partner-plan-radio-badge ${
                              isSelected ? "partner-plan-radio-badge--selected" : ""
                            }`}
                          >
                            {isSelected ? "✓ Selected" : "○ Select Plan"}
                          </span>
                        </div>
                      </div>

                      <div className="partner-plan-box__features">
                        {Array.isArray(plan.features) && plan.features.length > 0 ? (
                          plan.features.map((feat, fIdx) => (
                            <div key={fIdx} className="partner-plan-feature">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>{feat}</span>
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="partner-plan-feature">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>Verified Buyer & Tenant Lead Inquiries</span>
                            </div>
                            <div className="partner-plan-feature">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>Maximum Search Visibility in Selected City</span>
                            </div>
                            <div className="partner-plan-feature">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>Official Partner Badge & Trust Verification</span>
                            </div>
                            <div className="partner-plan-feature">
                              <Check size={16} className="text-success flex-shrink-0" />
                              <span>Dedicated Partner Portal & Lead Tracking CRM</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedPlan && currentPlanTrial && (
                <div className="partner-card mb-4 bg-light border-0 p-3 rounded-3 d-flex align-items-center gap-3">
                  <div className="partner-icon-circle bg-success text-white p-2 rounded-circle">
                    <Zap size={20} />
                  </div>
                  <div>
                    <div className="fw-bold text-dark" style={{ fontSize: "0.9375rem" }}>
                      {currentPlanTrial}-Day Free Trial Included
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.8125rem" }}>
                      Your trial will start immediately upon activation. You will get full access to your partner dashboard with no payment required today.
                    </div>
                  </div>
                </div>
              )}

              <div className="partner-btn-group mt-3">
                <button
                  type="button"
                  className="partner-btn partner-btn--outline"
                  onClick={() => navigate("/partner/onboarding")}
                  disabled={isActivating}
                >
                  <ArrowLeft size={16} />
                  <span>Back to Service</span>
                </button>
                <button
                  type="button"
                  className="partner-btn partner-btn--primary partner-btn--lg"
                  disabled={!selectedPlan || isActivating}
                  onClick={handleStartTrialAndProceed}
                >
                  {isActivating ? (
                    <span className="partner-spinner">Activating Trial & Opening Dashboard...</span>
                  ) : (
                    <>
                      <span>
                        {currentPlanTrial
                          ? `Start ${currentPlanTrial}-Day Trial & Open Dashboard`
                          : "Activate Plan & Open Dashboard"}
                      </span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerSubscription;
